import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { get, merge, isNil } from 'lodash';
import { Stack } from 'office-ui-fabric-react/lib/Stack';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { TextField as FluentTextField } from 'office-ui-fabric-react/lib/TextField';
import Callout from '../Callout';
import theme from '../../theme.style';
import styles from './TextField.style';

export const numWithCommasFormatter = (value, allowNegative) => {
  if (allowNegative && value === '-') {
    return value;
  }
  if (
    Number.isNaN(Number(value.replace(/,/g, ''))) ||
    (!allowNegative && Number(value.replace(/,/g, '')) < 0)
  ) {
    return undefined;
  }

  let splitVal = value.toString().split('.');
  splitVal[0] = Number(splitVal[0].replace(/,/g, '')).toLocaleString();
  splitVal = splitVal.join('.');

  return splitVal;
};

const TextField = ({
  field: { name, value, onChange },
  form: { errors = {}, isSubmitting = false, setFieldTouched },
  label,
  placeholder,
  callout,
  required,
  disabled,
  className,
  formatter,
  allowNegative = true,
  preventDefaultOnEnter,
  onUpdate = () => {},
  ...other
}) => {
  const [text, setText] = useState();
  useEffect(() => {
    setText(isNil(value) ? '' : value);
  }, [value]);

  const errorMessage = get(errors, name);
  let textFieldStyles = styles;
  if (errorMessage) {
    textFieldStyles = merge({}, styles, {
      fieldGroup: {
        borderColor: theme.palette.accentError,
      },
    });
  }

  const handleKeyDown = e => {
    if (preventDefaultOnEnter && e.keyCode === 13) {
      e.preventDefault();
      return false;
    }
    return true;
  };

  const TextFieldLabel = () =>
    label && (
      <Stack horizontal verticalAlign="start">
        <Label>{label}</Label>
        {required && <Text styles={styles.asterisk}>*</Text>}
        {callout && <Callout content={callout} />}
      </Stack>
    );

  return (
    <FluentTextField
      name={name}
      value={typeof text === 'object' ? text.val : text}
      onChange={(_e, val) => {
        let newVal = val;
        if (formatter) {
          newVal = formatter(val, allowNegative);
          if (!newVal) {
            return;
          }
        }
        setText(newVal);
        setFieldTouched(name);
        onUpdate(newVal);
      }}
      onBlur={e => onChange(e, text)}
      onKeyDown={handleKeyDown}
      onRenderLabel={TextFieldLabel}
      placeholder={placeholder || label}
      disabled={disabled || isSubmitting}
      borderless={disabled}
      errorMessage={errorMessage}
      ariaLabel={label}
      aria-required={required}
      className={className}
      styles={textFieldStyles}
      {...other}
    />
  );
};

TextField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    setFieldTouched: PropTypes.func,
  }),
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  callout: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string,
  formatter: PropTypes.func,
  allowNegative: PropTypes.bool,
  preventDefaultOnEnter: PropTypes.bool,
  onUpdate: PropTypes.func,
};

TextField.defaultProps = {
  className: '',
  formatter: null,
  allowNegative: true,
  required: false,
  label: null,
  placeholder: null,
  callout: null,
  disabled: false,
  preventDefaultOnEnter: false,
  onUpdate: () => {},
  form: {
    touched: {},
    errors: {},
    isSubmitting: false,
    setFieldTouched: () => {},
  },
};

export default TextField;
